import React from "react";
import { Space, Avatar } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faClock } from "@fortawesome/free-solid-svg-icons";
import Table from "../../commonComponents/Table";
import TablePagination from "../../commonComponents/Pagination";
import { DisputesTableProps, Dispute } from "./types";
import { ColumnsType } from "antd/es/table";
import { useTheme } from "../../context/ThemeContext";
import {
  ActionIconButton,
  CustomerName,
  CustomerEmail,
  TableText,
  ClockIconWrapper,
  DueByText,
} from "./Styles";

const DisputesTable: React.FC<DisputesTableProps> = ({
  disputes,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  getStatusBadge,
  isUrgent,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const columns: ColumnsType<Dispute> = [
    {
      title: "CUSTOMER",
      key: "customer",
      fixed: "left",
      width: 200,
      render: (_, record) => {
        const customerName = record.evidence?.customerName || record.order?.user_name || "Unknown";
        return (
          <Space>
            <Avatar>{customerName.charAt(0).toUpperCase()}</Avatar>
            <div>
              <CustomerName>{customerName}</CustomerName>
              {record.evidence?.customerEmail && (
                <CustomerEmail>
                  {record.evidence.customerEmail}
                </CustomerEmail>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: "AMOUNT",
      key: "amount",
      width: 150,
      render: (_, record) => (
        <TableText>
          ${parseFloat(String(record.amount || 0)).toFixed(2)} {record.currency?.toUpperCase() || ""}
        </TableText>
      ),
    },
    {
      title: "REASON",
      key: "reason",
      width: 120,
      render: (_, record) => (
        <TableText>
          {record.reason
            ? record.reason.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
            : "N/A"}
        </TableText>
      ),
    },
    {
      title: "STATUS",
      key: "status",
      width: 150,
      render: (_, record) => getStatusBadge(record.status),
    },
    {
      title: "DUE BY",
      key: "dueBy",
      width: 150,
      render: (_, record) => {
        const isUrgentDispute = isUrgent(record.status);
        if (!record.dueBy) return <TableText>N/A</TableText>;
        return (
          <Space align="center">
            <ClockIconWrapper isUrgent={isUrgentDispute}>
              <FontAwesomeIcon icon={faClock} fontSize={14} />
            </ClockIconWrapper>
            <DueByText isUrgent={isUrgentDispute}>
              {new Date(record.dueBy).toLocaleDateString()}
            </DueByText>
          </Space>
        );
      },
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: 100    ,
      render: (_, record) => {
        const isUrgentDispute = isUrgent(record.status);
        return (
          <Space size="small">
            <ActionIconButton
              onClick={() => onViewDetails(record)}
              iconColor={isUrgentDispute ? "#f5222d" : "#2D6CCA"}
            >
              <FontAwesomeIcon 
                icon={faEye} 
                fontSize={14} 
                color={isUrgentDispute ? "#f5222d" : (isDarkMode ? "#fff" : "#222222")} 
              />
            </ActionIconButton>
          </Space>
        );
      },
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      onPageSizeChange(size);
    }
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  // Custom row className for urgent disputes
  const getRowClassName = (record: Dispute) => {
    return isUrgent(record.status) ? "urgent-dispute-row" : "";
  };

  return (
    <>
      <Table<Dispute>
        columns={columns}
        dataSource={disputes}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{x: true, y: 600}}
        emptyText="No disputes found"
        headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
        removeVerticalBorders={true}
        thPadding='15px 10px'
        tdPadding='10px 10px'
        firstChildBorderRadius='8px'
        border='0'
        borderBottomLeftRight={true}
        rowClassName={getRowClassName}
        theme={isDarkMode ? "dark" : "light"}
      />
      {disputes.length > 0 && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total records"
          padding="16px 12px 0px 12px"
        />
      )}
    </>
  );
};

export default DisputesTable;

